import React, { useEffect, useMemo, useState } from "react";
import {
  Autocomplete,
  Avatar,
  Box,
  Button,
  CircularProgress,
  Chip,
  Dialog,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  Stack,
  TextField,
  Typography
} from "@mui/material";
import { ArrowBack, DeleteOutline, PersonRemove, PeopleOutline } from "@mui/icons-material";
import { useConfirm } from "material-ui-confirm";
import {
  Channel,
  ChannelHeader,
  MessageComposer,
  MessageList,
  Thread,
  useChatContext,
  Window,
  WithComponents,
} from "stream-chat-react";
import api from "../../utils/api";
import QuoteReplyMessageActions, {
  channelMessageActions,
} from "./QuoteReplyMessageActions";
import { apiUrls } from "../../utils/urls";
import { capitalize } from "../../utils/formatting";
import { useProfile } from "../../context/ProfileContext";
import { groups } from "../../constants/auth";

export default function ChatWindow({ isMobile = false, onBack = () => { } }) {
  const { channel: activeChannel, setActiveChannel, client } = useChatContext();
  const { profile } = useProfile();
  const confirm = useConfirm();
  const [membersDialogOpen, setMembersDialogOpen] = useState(false);
  const [memberSearchInput, setMemberSearchInput] = useState("");
  const [memberSearchTerm, setMemberSearchTerm] = useState("");
  const [candidateUsers, setCandidateUsers] = useState([]);
  const [selectedUsersToAdd, setSelectedUsersToAdd] = useState([]);
  const [loadingCandidates, setLoadingCandidates] = useState(false);
  const [addingMembers, setAddingMembers] = useState(false);
  const [updatingMemberId, setUpdatingMemberId] = useState(null);
  const [memberActionError, setMemberActionError] = useState("");
  const channelMembers = useMemo(
    () =>
      Object.values(activeChannel?.state?.members || {}).map((member) => ({
        id: String(member.user_id || member.user?.id || ""),
        name: member.user?.name || member.user?.id || member.user_id || "Unknown user",
        image: member.user?.image || "",
        online: Boolean(member.user?.online),
        role: capitalize(member.user.role)
      })),
    [activeChannel?.state?.members]
  );
  const creatorId = String(
    activeChannel?.data?.created_by?.id || activeChannel?.data?.created_by_id || ""
  );
  const currentUserId = String(client?.userID || "");
  const canDelete = creatorId && currentUserId && creatorId === currentUserId;
  const isEmployee = profile?.groups?.includes(groups.EMPLOYEES);
  const canManageMembers = isEmployee;
  const existingMemberIds = useMemo(
    () => new Set(channelMembers.map((member) => String(member.id))),
    [channelMembers]
  );
  const addableCandidates = useMemo(
    () => candidateUsers.filter((user) => !existingMemberIds.has(String(user.id))),
    [candidateUsers, existingMemberIds]
  );
  const messageActionOverrides = useMemo(
    () => ({ MessageActions: QuoteReplyMessageActions }),
    []
  );

  async function handleDeleteChat() {
    if (!activeChannel) {
      return;
    }
    confirm({
      title: "Delete chat?",
      description: "Delete this chat for all members? This cannot be undone.",
    })
      .then(async () => {
        try {
          await api.delete(apiUrls.messaging.channel(activeChannel.id));
          setActiveChannel(undefined);
        } catch (err) {
          // shared api helper shows error
        }
      })
      .catch(() => { });
  }

  useEffect(() => {
    if (!membersDialogOpen || !canManageMembers) {
      return undefined;
    }
    const timer = setTimeout(() => {
      setMemberSearchTerm(memberSearchInput.trim());
    }, 300);
    return () => clearTimeout(timer);
  }, [memberSearchInput, membersDialogOpen, canManageMembers]);

  useEffect(() => {
    if (!membersDialogOpen || !canManageMembers) {
      return;
    }
    let ignore = false;
    async function fetchCandidates() {
      setLoadingCandidates(true);
      try {
        const response = await api.get(apiUrls.messaging.users(), {
          params: {
            search_input: memberSearchTerm,
            page: 0,
            size: 25,
          },
        });
        if (!ignore) {
          setCandidateUsers(response?.data?.users || []);
        }
      } catch (err) {
        if (!ignore) {
          setMemberActionError(
            err?.response?.data?.error || err?.message || "Failed to load users."
          );
        }
      } finally {
        if (!ignore) {
          setLoadingCandidates(false);
        }
      }
    }
    fetchCandidates();
    return () => {
      ignore = true;
    };
  }, [memberSearchTerm, membersDialogOpen, canManageMembers]);

  async function refreshActiveChannelState() {
    if (!activeChannel) {
      return;
    }
    await activeChannel.query({ state: true, watch: true, messages: { limit: 25 } });
  }

  async function handleAddMembers() {
    if (!activeChannel || !canManageMembers) {
      return;
    }
    const selectedIds = selectedUsersToAdd
      .map((user) => Number(user.id))
      .filter((id) => Number.isInteger(id));
    if (!selectedIds.length) {
      setMemberActionError("Select at least one user to add.");
      return;
    }
    setAddingMembers(true);
    setMemberActionError("");
    try {
      await api.post(apiUrls.messaging.channel(activeChannel.id), {
        action: "add",
        member_ids: selectedIds,
      });
      await refreshActiveChannelState();
      setSelectedUsersToAdd([]);
    } catch (err) {
      setMemberActionError(
        err?.response?.data?.error || err?.message || "Failed to add members."
      );
    } finally {
      setAddingMembers(false);
    }
  }

  async function handleRemoveMember(member) {
    if (!activeChannel || !canManageMembers) {
      return;
    }
    const memberId = Number(member.id);
    if (!Number.isInteger(memberId)) {
      return;
    }
    confirm({
      title: "Remove member?",
      description: `Remove ${member.name} from this chat?`,
    })
      .then(async () => {
        setUpdatingMemberId(String(member.id));
        setMemberActionError("");
        try {
          await api.post(apiUrls.messaging.channel(activeChannel.id), {
            action: "remove",
            member_ids: [memberId],
          });
          await refreshActiveChannelState();
        } catch (err) {
          setMemberActionError(
            err?.response?.data?.error || err?.message || "Failed to remove member."
          );
        } finally {
          setUpdatingMemberId(null);
        }
      })
      .catch(() => { });
  }

  if (!activeChannel) {
    return (
      <div style={{ flex: 1, display: "flex", alignItems: "center", justifyContent: "center" }}>
        <p>Select a conversation to get started.</p>
      </div>
    );
  }

  return (
    <Box
      sx={{
        flex: 1,
        minWidth: 0,
        minHeight: 0,
        display: "flex",
        flexDirection: "column",
        overflow: "hidden",
      }}
    >
      <Box
        sx={{
          p: 1,
          borderBottom: 1,
          borderColor: "divider",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
        }}
      >
        {isMobile ? (
          <Button size="small" startIcon={<ArrowBack />} onClick={onBack}>
            Back
          </Button>
        ) : (
          <Box />
        )}
        <Stack direction="row" spacing={1} alignItems="center">
          <Button
            size="small"
            variant="outlined"
            startIcon={<PeopleOutline />}
            onClick={() => setMembersDialogOpen(true)}
          >
            View Members
          </Button>
          {canDelete && (
            <Button
              size="small"
              color="error"
              variant="text"
              startIcon={<DeleteOutline />}
              onClick={handleDeleteChat}
            >
              Delete Chat
            </Button>
          )}
        </Stack>
      </Box>
      <Box
        sx={{
          flex: 1,
          minHeight: 0,
          display: "flex",
          overflow: "hidden",
          "& .str-chat__channel": { height: "100%", width: "100%" },
          "& .str-chat__main-panel": { height: "100%" },
          "& .str-chat__window": { height: "100%" },
          "& .str-chat__message-list": { minHeight: 0, position: "relative" },
          "& .str-chat__message-list-scroll": { minHeight: 0 },
        }}
      >
        <Channel channel={activeChannel}>
          <WithComponents overrides={messageActionOverrides}>
            <Window>
              <ChannelHeader />
              <MessageList messageActions={channelMessageActions} />
              <MessageComposer />
            </Window>
            <Thread />
          </WithComponents>
        </Channel>
      </Box>
      <Dialog
        open={membersDialogOpen}
        onClose={() => {
          setMembersDialogOpen(false);
          setMemberActionError("");
          setMemberSearchInput("");
          setMemberSearchTerm("");
          setSelectedUsersToAdd([]);
        }}
        fullWidth
        maxWidth="xs"
      >
        <DialogTitle>Channel Members ({channelMembers.length})</DialogTitle>
        <DialogContent dividers>
          {canManageMembers && (
            <Box sx={{ mb: 2 }}>
              <Typography variant="subtitle2" sx={{ mb: 1 }}>
                Add Members
              </Typography>
              <Stack direction="row" spacing={1} alignItems="flex-start">
                <Autocomplete
                  multiple
                  fullWidth
                  options={addableCandidates}
                  value={selectedUsersToAdd}
                  loading={loadingCandidates}
                  inputValue={memberSearchInput}
                  onInputChange={(_, value) => setMemberSearchInput(value)}
                  onChange={(_, value) => {
                    setSelectedUsersToAdd(value);
                    setMemberActionError("");
                  }}
                  filterOptions={(options) => options}
                  isOptionEqualToValue={(option, value) => option.id === value.id}
                  getOptionLabel={(option) => option?.name || option?.email || "Unknown user"}
                  noOptionsText={
                    loadingCandidates ? "Loading users..." : "No addable users found."
                  }
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      size="small"
                      label="Search users to add"
                      placeholder="Type name or email"
                    />
                  )}
                  renderOption={(props, option) => (
                    <li {...props}>
                      {option.name} ({option.email})
                    </li>
                  )}
                />
                <Button
                  variant="contained"
                  onClick={handleAddMembers}
                  disabled={addingMembers || !selectedUsersToAdd.length}
                  sx={{ minWidth: 88, mt: 0.25 }}
                >
                  {addingMembers ? <CircularProgress size={18} color="inherit" /> : "Add"}
                </Button>
              </Stack>
              <Divider sx={{ mt: 2 }} />
            </Box>
          )}
          {channelMembers.length === 0 ? (
            <Typography variant="body2" color="text.secondary">
              No members found for this channel.
            </Typography>
          ) : (
            <List disablePadding>
              {channelMembers.map((member, index) => (
                <React.Fragment key={member.id || `${member.name}-${index}`}>
                  <ListItem sx={{ px: 0 }}>
                    <ListItemAvatar>
                      <Avatar src={member.image} alt={member.name}>
                        {member.name?.charAt(0)?.toUpperCase() || "U"}
                      </Avatar>
                    </ListItemAvatar>
                    <ListItemText
                      primary={member.name}
                      secondary={member.role}
                      slotProps={{
                        primary: {
                          noWrap: true,
                        },
                        secondary: {
                          noWrap: true,
                        },
                      }}
                    />
                    <Chip
                      size="small"
                      label={member.online ? "Online" : "Offline"}
                      color={member.online ? "success" : "default"}
                      variant={member.online ? "filled" : "outlined"}
                    />
                    {canManageMembers && member.id !== creatorId && (
                      <IconButton
                        size="small"
                        color="error"
                        onClick={() => handleRemoveMember(member)}
                        disabled={updatingMemberId === member.id}
                      >
                        <PersonRemove fontSize="small" />
                      </IconButton>
                    )}
                  </ListItem>
                  {index < channelMembers.length - 1 && <Divider component="li" />}
                </React.Fragment>
              ))}
            </List>
          )}
          {memberActionError && (
            <Typography sx={{ color: "error.main", mt: 2 }}>
              {memberActionError}
            </Typography>
          )}
        </DialogContent>
      </Dialog>
    </Box>
  );
}
